import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Dropdown, Menu } from "antd";
import { CloseOutlined, DownloadOutlined, EditOutlined, SendOutlined, SettingOutlined } from "@ant-design/icons";
import { AppContext } from "../../AppContext";
import DesignApi from "../../api/DesignApi";
import MenuChatApi from "../../api/MenuChatApi";
import { env } from "../../env/env";
import { isDesignSubscription, isFreeUser } from "../../service/BillingService";
import {
  memberState,
  menuChatState,
  MenuChatChange,
  MenuChatDesign,
  MenuChatEffort,
  MenuChatMessage,
} from "../../state/atoms";
import { hasFeature, MENU_CHAT } from "../../util/features";
import Storage from "../../util/Storage";
import { getHeader } from "../../util/utils";
import { useDrawerDismiss } from "../account/page/data-kit/useDrawerDismiss";
import "./MenuChatDrawer.scss";

const SUGGESTIONS = [
  "Which items are missing a description?",
  "Fix the spelling mistakes on my menus",
  "Show me a preview of my latest menu",
  "What are my most expensive items?",
  "Which items have different prices on different menus?",
  "Suggest better descriptions for my appetizers",
  "List every item priced above $20",
  "Download the PDF of my latest menu",
];

const PREVIEW_HEIGHT = 1400;
const MAX_PREVIEW_PAGES = 4;

const FIELD_LABELS: Record<string, string> = {
  ITEM_NAME: "Name",
  ITEM_DESCRIPTION: "Description",
  ITEM_TAG: "Tag",
  ITEM_EXTRAS: "Extras",
  ITEM_PRICE: "Price",
  SECTION_NAME: "Section name",
  SECTION_NOTE: "Section note",
  TEXT_BLOCK: "Text",
  ITEM_ADD: "New item",
  ITEM_REMOVE: "Remove item",
  SECTION_REMOVE: "Remove section",
};

const EFFORTS: { value: MenuChatEffort; label: string }[] = [
  { value: "FAST", label: "Fast" },
  { value: "NORMAL", label: "Normal" },
  { value: "HIGH", label: "High" },
];

const CHANGE_STATE_LABELS: Record<string, string> = {
  applied: "Applied. Publish the menu to make it live.",
  partial: "Applied to some menus only.",
  failed: "Not applied.",
  dismissed: "Dismissed.",
};

function MenuChatChanges({
  message,
  onApply,
  onDismiss,
}: {
  message: MenuChatMessage;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const changes = message.changes ?? [];
  const state = message.changeState ?? "pending";
  const designs = Array.from(new Set(changes.map((change) => change.designName)));
  const target = (change: MenuChatChange) => {
    if (change.field === "TEXT_BLOCK") return "Text block";
    if (change.field === "ITEM_ADD") return `New item in ${change.sectionName || "section"}`;
    if (change.field === "ITEM_REMOVE") return `Remove item from ${change.sectionName || "section"}`;
    if (change.field === "SECTION_REMOVE") return "Remove section";
    return `${change.itemName || change.sectionName || "Section"} · ${FIELD_LABELS[change.field] ?? change.field}`;
  };
  const removes = (change: MenuChatChange) => change.field === "ITEM_REMOVE" || change.field === "SECTION_REMOVE";

  return (
    <div className="menu-chat-changes">
      {designs.map((design) => (
        <div key={design} className="menu-chat-change-group">
          <h3>{design}</h3>
          {changes
            .filter((change) => change.designName === design)
            .map((change, index) => (
              <div key={index} className="menu-chat-change">
                <div className="menu-chat-change-target">{target(change)}</div>
                {change.field !== "ITEM_ADD" && <div className="menu-chat-change-from">{change.from || "(empty)"}</div>}
                {!removes(change) && (
                  <div className={`menu-chat-change-to${change.field === "ITEM_ADD" ? " added" : ""}`}>
                    {change.to || "(empty)"}
                  </div>
                )}
              </div>
            ))}
        </div>
      ))}

      {(state === "pending" || state === "applying") && (
        <div className="menu-chat-change-actions">
          <button type="button" className="primary" onClick={onApply} disabled={state === "applying"}>
            {state === "applying" ? "Applying…" : `Apply ${changes.length === 1 ? "change" : `${changes.length} changes`}`}
          </button>
          <button type="button" onClick={onDismiss} disabled={state === "applying"}>
            Dismiss
          </button>
        </div>
      )}
      {CHANGE_STATE_LABELS[state] && <div className={`menu-chat-change-state ${state}`}>{CHANGE_STATE_LABELS[state]}</div>}
      {message.changeNotes?.map((note) => (
        <div key={note} className="menu-chat-change-note">
          {note}
        </div>
      ))}
    </div>
  );
}

function renderEmphasis(text: string) {
  return text.split(/(\*\*[^*\n]+\*\*)/g).map((part, index) =>
    part.length > 4 && part.startsWith("**") && part.endsWith("**") ? (
      <strong key={index}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );
}

function MenuChatDesignCard({ design }: { design: MenuChatDesign }) {
  const member = useRecoilValue(memberState);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [missingPages, setMissingPages] = useState<number[]>([]);

  const pageCount = Math.min(Math.max(design.pageCount, 1), MAX_PREVIEW_PAGES);
  const requested = design.page > 0 ? [design.page] : Array.from({ length: pageCount }, (_, index) => index + 1);
  const pages = requested.filter((page) => !missingPages.includes(page));
  const canDownload = member.downloads || member.itemLibraryOnly || member.dataOnly || member.updateDrd || member.viewOnly;

  const thumb = (page: number) =>
    `${env.rendererPath}/design/thumbnail/${design.id}?height=${PREVIEW_HEIGHT}&page=${page}&update=${design.updated}`;

  const open = () => window.open(`${env.timberContextPath}/design?id=${design.id}`, "_blank");

  const download = async () => {
    if (isFreeUser(member) && !design.free) {
      const upgrade = isDesignSubscription(member) ? window.MHM.ProModal.switchToProWithOverview : window.MHM.ProModal.upgrade;
      upgrade({ memberId: member.id, onSuccess: () => window.location.reload() });
      return;
    }

    setDownloading(true);
    setDownloadFailed(false);
    try {
      await DesignApi.registerDownload(design.id);
      const response = await DesignApi.downloadPdf(design.id, false);
      if (!response.ok) throw new Error(`PDF download failed with ${response.status}`);

      const headers: string[][] = [];
      response.headers.forEach((value, key) => headers.push([key, value]));
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.download = getHeader(headers, "filename") || `${design.name || "menu"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="menu-chat-design">
      <h3>
        {design.name}
        {design.page > 0 && design.pageCount > 1 ? ` · page ${design.page} of ${design.pageCount}` : ""}
      </h3>
      {pages.length > 0 && (
        <div className={`menu-chat-design-pages${pages.length === 1 ? " single" : ""}`}>
          {pages.map((page) => (
            <a key={page} href={thumb(page)} target="_blank" rel="noreferrer" title="Open full size">
              <img
                src={thumb(page)}
                alt={`${design.name}, page ${page}`}
                loading="lazy"
                onError={() => setMissingPages((current) => [...current, page])}
              />
            </a>
          ))}
        </div>
      )}
      {design.page === 0 && design.pageCount > MAX_PREVIEW_PAGES && (
        <div className="menu-chat-design-more">Showing the first {MAX_PREVIEW_PAGES} of {design.pageCount} pages.</div>
      )}
      <div className="menu-chat-design-actions">
        <button type="button" onClick={open}>
          <EditOutlined /> Open in editor
        </button>
        {canDownload && (
          <button type="button" onClick={download} disabled={downloading}>
            <DownloadOutlined /> {downloading ? "Preparing PDF…" : "Download PDF"}
          </button>
        )}
      </div>
      {downloadFailed && <div className="menu-chat-change-note">The PDF could not be downloaded. Try again.</div>}
    </div>
  );
}

function MenuChatEffortPicker({ effort, onChange }: { effort: MenuChatEffort; onChange: (effort: MenuChatEffort) => void }) {
  const options = (
    <Menu className="menu-chat-effort-menu" selectedKeys={[effort]} onClick={({ key }) => onChange(key as MenuChatEffort)}>
      {EFFORTS.map((option) => (
        <Menu.Item key={option.value}>
          <div className="menu-chat-effort-label">{option.label}</div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown
      overlay={options}
      trigger={["click"]}
      placement="bottomRight"
      getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
    >
      <button type="button" className="menu-chat-effort" aria-label="Effort" title="Effort">
        <SettingOutlined />
      </button>
    </Dropdown>
  );
}

function MenuChatGreeting({ name, sending, onPick }: { name: string; sending: boolean; onPick: (text: string) => void }) {
  const greeting = name ? `Hello, ${name}` : "Hello";

  return (
    <div className="menu-chat-greeting">
      <h1 aria-label={greeting}>
        {greeting.split("").map((letter, index) => (
          <span key={index} aria-hidden="true" style={{ animationDelay: `${200 + index * 45}ms` }}>
            {letter === " " ? "\u00A0" : letter}
          </span>
        ))}
      </h1>
      <p>Ask about your menus, preview them, or tell me what to change.</p>
      <div className="menu-chat-suggestions">
        {SUGGESTIONS.map((suggestion, index) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => onPick(suggestion)}
            disabled={sending}
            style={{ animationDelay: `${900 + index * 70}ms` }}
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
}

function MenuChatPanel({ locationId, locationName }: { locationId: number; locationName: string }) {
  const [chat, setChat] = useRecoilState(menuChatState);
  const member = useRecoilValue(memberState);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [omittedDesigns, setOmittedDesigns] = useState<string[]>([]);
  const [effort, setEffort] = useState<MenuChatEffort>(Storage.getMenuChatEffort);
  const threadEnd = useRef<HTMLDivElement>(null);
  const activeLocation = useRef(locationId);

  const { closing, dismiss } = useDrawerDismiss();
  const close = useCallback(
    () => dismiss(() => setChat((current) => ({ ...current, open: false }))),
    [dismiss, setChat]
  );

  useEffect(() => {
    activeLocation.current = locationId;
    if (chat.locationId === locationId) return;

    setChat((current) => ({ ...current, locationId, messages: [] }));
    setOmittedDesigns([]);
    setError("");
    setLoading(true);
    MenuChatApi.getMessages(locationId)
      .then((resp) => {
        if (activeLocation.current !== locationId) return;
        setChat((current) => ({ ...current, messages: resp.data.messages ?? [] }));
      })
      .catch(() => {
        if (activeLocation.current === locationId) setError("The conversation could not be loaded.");
      })
      .finally(() => {
        if (activeLocation.current === locationId) setLoading(false);
      });
  }, [locationId, chat.locationId, setChat]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ block: "end" });
  }, [chat.messages, sending]);

  const changeEffort = (next: MenuChatEffort) => {
    setEffort(next);
    Storage.setMenuChatEffort(next);
  };

  const send = (text: string) => {
    const content = text.trim();
    if (!content || sending || loading) return;

    const previous = chat.messages;
    setChat((current) => ({ ...current, messages: [...previous, { role: "user", content }] }));
    setDraft("");
    setError("");
    setSending(true);

    MenuChatApi.sendMessage(locationId, content, effort)
      .then((resp) => {
        if (activeLocation.current !== locationId) return;
        setOmittedDesigns(resp.data.omittedDesigns ?? []);
        setChat((current) => ({ ...current, messages: [...previous, ...(resp.data.messages ?? [])] }));
      })
      .catch(() => {
        if (activeLocation.current !== locationId) return;
        setChat((current) => ({ ...current, messages: previous }));
        setDraft(content);
        setError("The assistant could not answer. Try again.");
      })
      .finally(() => setSending(false));
  };

  const updateMessage = (id: number, patch: Partial<MenuChatMessage>) =>
    setChat((current) => ({
      ...current,
      messages: current.messages.map((message) => (message.id === id ? { ...message, ...patch } : message)),
    }));

  const applyChanges = (id: number) => {
    updateMessage(id, { changeState: "applying", changeNotes: [] });

    MenuChatApi.applyChanges(locationId, id)
      .then((resp) => {
        if (activeLocation.current === locationId) updateMessage(id, resp.data);
      })
      .catch((e) => {
        if (activeLocation.current !== locationId) return;
        const forbidden = e?.response?.status === 403;
        updateMessage(id, {
          changeState: "pending",
          changeNotes: [
            forbidden
              ? "You don't have permission to edit menus at this location."
              : "The changes could not be applied. Try again.",
          ],
        });
      });
  };

  const dismissChanges = (id: number) => {
    updateMessage(id, { changeState: "dismissed", changeNotes: [] });
    MenuChatApi.dismissChanges(locationId, id).catch(() => undefined);
  };

  const clear = () => {
    if (sending || loading) return;
    MenuChatApi.clearMessages(locationId)
      .then(() => {
        if (activeLocation.current !== locationId) return;
        setChat((current) => ({ ...current, messages: [] }));
        setOmittedDesigns([]);
        setError("");
      })
      .catch(() => {
        if (activeLocation.current === locationId) setError("The conversation could not be cleared.");
      });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  const firstName = (member.name ?? "").trim().split(/\s+/)[0] ?? "";
  const showGreeting = !loading && chat.messages.length === 0;

  return (
    <aside className={`menu-chat-overlay${closing ? " closing" : ""}`} role="dialog" aria-label="Menu Assistant">
      <header className="menu-chat-header">
        <div>
          <h2>Menu Assistant</h2>
          <p>{locationName}</p>
        </div>
        <div className="menu-chat-header-actions">
          {chat.messages.length > 0 && (
            <button type="button" className="menu-chat-clear" onClick={clear} disabled={sending || loading}>
              Clear chat
            </button>
          )}
          <MenuChatEffortPicker effort={effort} onChange={changeEffort} />
          <button type="button" className="menu-chat-close" onClick={close} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
      </header>

      <div className="menu-chat-scroll">
        <div className={`menu-chat-thread${showGreeting ? " empty" : ""}`}>
          {showGreeting && <MenuChatGreeting name={firstName} sending={sending} onPick={send} />}

          {chat.messages.map((message, index) => (
            <div key={message.id ?? `draft-${index}`} className={`menu-chat-message ${message.role}`}>
              {message.role === "assistant" ? renderEmphasis(message.content) : message.content}
              {!!message.changes?.length && message.id !== undefined && (
                <MenuChatChanges
                  message={message}
                  onApply={() => applyChanges(message.id as number)}
                  onDismiss={() => dismissChanges(message.id as number)}
                />
              )}
              {message.designs?.map((design) => (
                <MenuChatDesignCard key={`${design.id}-${design.page}-${design.updated}`} design={design} />
              ))}
            </div>
          ))}

          {loading && <div className="menu-chat-message assistant pending">Loading the conversation…</div>}
          {sending && (
            <div className="menu-chat-message assistant pending">
              <span className="menu-chat-typing" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              Reading your menus…
            </div>
          )}
          {error && <div className="menu-chat-error">{error}</div>}
          {omittedDesigns.length > 0 && (
            <div className="menu-chat-notice">
              Too much menu content to read at once. Not included: {omittedDesigns.join(", ")}.
            </div>
          )}
          <div ref={threadEnd} />
        </div>
      </div>

      <footer className="menu-chat-composer">
        <div className="menu-chat-composer-box">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about your menus or request a change"
            rows={2}
            maxLength={4000}
            autoFocus
          />
          <button
            type="button"
            onClick={() => send(draft)}
            disabled={sending || loading || !draft.trim()}
            aria-label="Send"
          >
            <SendOutlined />
          </button>
        </div>
      </footer>
    </aside>
  );
}

export default function MenuChatDrawer() {
  const member = useRecoilValue(memberState);
  const chat = useRecoilValue(menuChatState);
  const { location } = useContext(AppContext);

  if (!chat.open || !hasFeature(member, MENU_CHAT) || !location?.internalId) return null;

  return <MenuChatPanel locationId={location.internalId} locationName={location.name ?? ""} />;
}
